import {createSlice, type PayloadAction} from '@reduxjs/toolkit'
import type {
  DeviceConfig,
  FrontFaceFeatureConfig,
  SceneFullConfig,
  SceneLightConfig,
  SideEdgeFeatures,
  SideEdgeFeatureConfig,
  SideEdge,
} from '../types'
import type {ScreenText} from '../../types/textTypes'
import {createScreenText} from '../../types/textTypes'
import type {SceneText3D} from '../../types/text3dTypes'
import {createSceneText3D} from '../../types/text3dTypes'
import type {ScreenSprite, SceneSprite3D, Sprite3DMaterial} from '../../types/spriteTypes'
import {createScreenSprite, createSceneSprite3D} from '../../types/spriteTypes'
import type {CustomDecal, CustomDecalPlacement} from '../../types/customDecalTypes'
import {
  createCustomDecal,
  customDecalPlaneSize,
  maxCustomDecalSize,
  MIN_CUSTOM_DECAL_SIZE,
} from '../../types/customDecalTypes'
import {FRONT_FACE_GAP} from '../workerGeo/helpers'
import {
  MAX_EXPORT_DIMENSION,
  MIN_EXPORT_DIMENSION,
  MIN_CAROUSEL_COUNT,
  MAX_CAROUSEL_COUNT,
  DEFAULT_CAROUSEL_COUNT,
  MIN_CAROUSEL_GAP_PCT,
  MAX_CAROUSEL_GAP_PCT,
  DEFAULT_CAROUSEL_GAP_PCT,
} from '../../constants/exportPresets'

// Clamp export dimension to the safety-net range. Mirrors the BE cap.
const clampExportDim = (n: number): number => {
  if (!Number.isFinite(n)) return MIN_EXPORT_DIMENSION
  return Math.min(MAX_EXPORT_DIMENSION, Math.max(MIN_EXPORT_DIMENSION, Math.round(n)))
}

// Clamp carousel slice count to product bounds.
const clampCarouselCount = (n: number): number => {
  if (!Number.isFinite(n)) return DEFAULT_CAROUSEL_COUNT
  return Math.min(MAX_CAROUSEL_COUNT, Math.max(MIN_CAROUSEL_COUNT, Math.round(n)))
}

// Clamp carousel gap (percentage of slice width) to product bounds.
const clampCarouselGap = (n: number): number => {
  if (!Number.isFinite(n)) return DEFAULT_CAROUSEL_GAP_PCT
  return Math.min(MAX_CAROUSEL_GAP_PCT, Math.max(MIN_CAROUSEL_GAP_PCT, Math.round(n)))
}

// Clamp device Z translation to [0, DEVICE_POSITION_Z_MAX]. The device
// only ever moves forward (toward camera) from its baseline at z = 0;
// 50 units is enough to push it visibly in front of 3D text / sprites
// without breaking the orbit-camera framing.
export const DEVICE_POSITION_Z_MAX = 50
const clampDevicePositionZ = (n: number): number => {
  if (!Number.isFinite(n)) return 0
  return Math.min(DEVICE_POSITION_Z_MAX, Math.max(0, n))
}

// Structural defaults for a fresh scene. The real defaults always come from
// the backend (GET /defaults/project) and overwrite these via loadProject.
// These values exist only so the slice has well-typed initial values for
// every field — they are not the canonical defaults shown to the user.
const SCENE_BASE_DEFAULTS = {
  bgMode: 'gradient' as const,
  bgGradientStops: [
    {id: 'stop-0', color: '#ffffff', position: 0},
    {id: 'stop-1', color: '#e0e7ff', position: 1},
  ],
  bgAngle: 0,
  ambientIntensity: 5,
  ambientColor: '#ffffff',
  envPreset: 'city',
  envMapIntensity: 50,
  groundDistance: 0,
  exportWidth: 1320,
  exportHeight: 2868,
  cameraPosition: [0, 0, 230] as number[],
  cameraRotation: [0, 0, 0] as number[],
  screenImageUrl: '/sample.jpg',
  lights: [
    {
      id: 'key',
      label: 'Key Light',
      type: 'directional',
      color: '#ffffff',
      intensity: 40,
      position: [10, -10, 35],
      shadowIntensity: 100,
      shadowSoftness: 50,
    },
    {
      id: 'fill',
      label: 'Fill Light',
      type: 'rectarea',
      color: '#ffffff',
      intensity: 7,
      position: [-40, -30, 100],
      width: 80,
      height: 80,
      shadowIntensity: 0,
      shadowSoftness: 0,
    },
    {
      id: 'rim',
      label: 'Rim Light',
      type: 'rectarea',
      color: '#ffffff',
      intensity: 8,
      position: [50, 80, -100],
      width: 60,
      height: 60,
      shadowIntensity: 0,
      shadowSoftness: 0,
    },
  ] as SceneLightConfig[],
}

export const CUSTOM_DEVICE_NAME = 'Custom'

// ── Types ─────────────────────────────────────────────────────────

export type BgMode = 'gradient' | 'image'
export type BgImageFit = 'fill' | 'contain' | 'cover'
export type BodyNormalPattern = 'brushed' | 'noise'

export interface GradientStop {
  id: string
  color: string
  position: number // 0–1
}

/** Combined scene + device state for a single scene */
export interface SingleSceneState {
  // ── Active presets ─────────────────────────────────────────────
  activeDeviceName: string
  // ── Background ────────────────────────────────────────────────
  bgMode: BgMode
  bgGradientStops: GradientStop[]
  bgAngle: number
  bgImageKey: string
  bgImageUrl: string
  bgImageFit: BgImageFit
  bgImageOffsetX: number
  bgImageOffsetY: number
  bgImageZoom: number
  // ── Lighting ──────────────────────────────────────────────────
  lights: SceneLightConfig[]
  ambientIntensity: number
  ambientColor: string
  // ── Environment ───────────────────────────────────────────────
  envPreset: string
  envMapIntensity: number
  // ── Ground / shadow catcher ───────────────────────────────────
  groundDistance: number
  // ── Screen image ──────────────────────────────────────────────
  screenImageKey: string
  screenImageUrl: string
  // ── Export settings ───────────────────────────────────────────
  exportWidth: number
  exportHeight: number
  exportFrameEnabled: boolean
  exportCarouselEnabled: boolean
  exportCarouselCount: number
  exportCarouselGap: number
  // ── Device transform ──────────────────────────────────────────
  /** In-plane rotation of the device around camera-facing Z, in degrees [0..360). */
  deviceRotationZ: number
  /** Forward/back translation of the device along the camera-facing Z axis, in world units. */
  devicePositionZ: number
  // ── 2D Texts ──────────────────────────────────────────────────
  texts: ScreenText[]
  // ── 3D Texts ──────────────────────────────────────────────────
  texts3d: SceneText3D[]
  // ── 2D Sprites (screen-space image overlays) ──────────────────
  sprites2d: ScreenSprite[]
  // ── 3D Sprites (world-space image planes) ─────────────────────
  sprites3d: SceneSprite3D[]
  // ── Custom decals (user images on the device front face / side edges) ──
  customDecals: CustomDecal[]
  // ── Camera ────────────────────────────────────────────────────
  cameraPosition: number[]
  cameraRotation: number[]
  // ── Device dimensions ─────────────────────────────────────────
  deviceWidth: number
  deviceHeight: number
  deviceDepth: number
  deviceCornerRadius: number
  deviceEdgeBevel: number
  frontFaceInset: number
  displayCornerRadius: number
  displayInsetX: number
  displayInsetY: number
  // Screen-glass reflection blend, 0 = unlit, 1 = full glass
  displayReflection: number
  frontFaceFeatures: FrontFaceFeatureConfig[]
  sideEdgeFeatures: SideEdgeFeatures
  /** Monotonic counter — incremented on committed dimension changes to trigger worker */
  geometryVersion: number
  // ── Device materials — Body ───────────────────────────────────
  bodyColor: string
  bodyMetalness: number
  bodyRoughness: number
  bodyClearcoat: number
  bodyClearcoatRoughness: number
  bodyReflectivity: number
  // ── Device materials — Front face ─────────────────────────────
  frontFaceColor: string
  frontFaceMetalness: number
  frontFaceRoughness: number
  frontFaceClearcoat: number
  frontFaceClearcoatRoughness: number
  frontFaceReflectivity: number
  // ── Device materials — Buttons ────────────────────────────────
  buttonsColor: string
  buttonsMetalness: number
  buttonsRoughness: number
  buttonsClearcoat: number
  buttonsClearcoatRoughness: number
  buttonsReflectivity: number
  // ── Device materials — Antennas ───────────────────────────────
  antennasColor: string
  antennasMetalness: number
  antennasRoughness: number
  antennasClearcoat: number
  antennasClearcoatRoughness: number
  antennasReflectivity: number
  // ── Device materials — Screws ─────────────────────────────────
  screwsColor: string
  screwsMetalness: number
  screwsRoughness: number
  screwsClearcoat: number
  screwsClearcoatRoughness: number
  screwsReflectivity: number
  // ── Device materials — Front speaker ──────────────────────────
  frontSpeakerColor: string
  frontSpeakerMetalness: number
  frontSpeakerRoughness: number
  frontSpeakerClearcoat: number
  frontSpeakerClearcoatRoughness: number
  frontSpeakerReflectivity: number
  // ── Device materials — Speaker grill ──────────────────────────
  speakerGrillColor: string
  speakerGrillMetalness: number
  speakerGrillRoughness: number
  speakerGrillClearcoat: number
  speakerGrillClearcoatRoughness: number
  speakerGrillReflectivity: number
  // ── Device materials — Charging connector tongue ──────────────
  chargingConnectorTongueColor: string
  chargingConnectorTongueMetalness: number
  chargingConnectorTongueRoughness: number
  chargingConnectorTongueClearcoat: number
  chargingConnectorTongueClearcoatRoughness: number
  chargingConnectorTongueReflectivity: number
  // ── Device materials — Charging connector rim ─────────────────
  chargingConnectorRimColor: string
  chargingConnectorRimMetalness: number
  chargingConnectorRimRoughness: number
  chargingConnectorRimClearcoat: number
  chargingConnectorRimClearcoatRoughness: number
  chargingConnectorRimReflectivity: number
  // ── Device body surface textures ────────────────────────────────
  bodyNoiseIntensity: number
  bodyNormalStrength: number
  bodyNormalPattern: BodyNormalPattern
  buttonsNoiseIntensity: number
  buttonsNormalStrength: number
  buttonsNormalPattern: BodyNormalPattern
}

interface ScenesState {
  activeSceneIndex: number
  scenes: SingleSceneState[]
  sceneNames: string[]
}

// ── Default scene factory ─────────────────────────────────────────

/** Create a scene from device config and scene defaults */
export const createDefaultScene = (deviceConfig: DeviceConfig): SingleSceneState => ({
  activeDeviceName: deviceConfig.name,
  bgMode: SCENE_BASE_DEFAULTS.bgMode,
  bgGradientStops: SCENE_BASE_DEFAULTS.bgGradientStops,
  bgAngle: SCENE_BASE_DEFAULTS.bgAngle,
  bgImageKey: '',
  bgImageUrl: '',
  bgImageFit: 'cover',
  bgImageOffsetX: 0,
  bgImageOffsetY: 0,
  bgImageZoom: 1,
  lights: SCENE_BASE_DEFAULTS.lights,
  ambientIntensity: SCENE_BASE_DEFAULTS.ambientIntensity,
  ambientColor: SCENE_BASE_DEFAULTS.ambientColor,
  envPreset: SCENE_BASE_DEFAULTS.envPreset,
  envMapIntensity: SCENE_BASE_DEFAULTS.envMapIntensity,
  groundDistance: SCENE_BASE_DEFAULTS.groundDistance,
  screenImageKey: '',
  screenImageUrl: SCENE_BASE_DEFAULTS.screenImageUrl,
  exportWidth: SCENE_BASE_DEFAULTS.exportWidth,
  exportHeight: SCENE_BASE_DEFAULTS.exportHeight,
  exportFrameEnabled: false,
  exportCarouselEnabled: false,
  exportCarouselCount: DEFAULT_CAROUSEL_COUNT,
  exportCarouselGap: DEFAULT_CAROUSEL_GAP_PCT,
  deviceRotationZ: 0,
  devicePositionZ: 0,
  texts: [],
  texts3d: [],
  sprites2d: [],
  sprites3d: [],
  // Seed decals shipped with the device (deep-cloned so scenes don't share refs)
  customDecals: deviceConfig.customDecals
    ? (JSON.parse(JSON.stringify(deviceConfig.customDecals)) as CustomDecal[])
    : [],
  cameraPosition: SCENE_BASE_DEFAULTS.cameraPosition,
  cameraRotation: SCENE_BASE_DEFAULTS.cameraRotation,
  // Device dimensions
  deviceWidth: deviceConfig.body.width,
  deviceHeight: deviceConfig.body.height,
  deviceDepth: deviceConfig.body.depth,
  deviceCornerRadius: deviceConfig.body.cornerRadius,
  deviceEdgeBevel: deviceConfig.body.edgeBevel,
  frontFaceInset: deviceConfig.frontFace.inset,
  displayCornerRadius: deviceConfig.display.cornerRadius,
  displayInsetX: deviceConfig.display.insetX,
  displayInsetY: deviceConfig.display.insetY,
  displayReflection: 0,
  frontFaceFeatures: deviceConfig.frontFaceFeatures,
  sideEdgeFeatures: deviceConfig.sideEdgeFeatures,
  geometryVersion: 0,
  // Device materials — Body
  bodyColor: deviceConfig.materials.body.color,
  bodyMetalness: deviceConfig.materials.body.metalness,
  bodyRoughness: deviceConfig.materials.body.roughness,
  bodyClearcoat: deviceConfig.materials.body.clearcoat,
  bodyClearcoatRoughness: deviceConfig.materials.body.clearcoatRoughness,
  bodyReflectivity: 0.5,
  // Device materials — Front face
  frontFaceColor: deviceConfig.materials.frontFace.color,
  frontFaceMetalness: deviceConfig.materials.frontFace.metalness,
  frontFaceRoughness: deviceConfig.materials.frontFace.roughness,
  frontFaceClearcoat: deviceConfig.materials.frontFace.clearcoat,
  frontFaceClearcoatRoughness: deviceConfig.materials.frontFace.clearcoatRoughness,
  frontFaceReflectivity: deviceConfig.materials.frontFace.reflectivity,
  // Device materials — Buttons (default to body material)
  buttonsColor: deviceConfig.materials.body.color,
  buttonsMetalness: deviceConfig.materials.body.metalness,
  buttonsRoughness: deviceConfig.materials.body.roughness,
  buttonsClearcoat: deviceConfig.materials.body.clearcoat,
  buttonsClearcoatRoughness: deviceConfig.materials.body.clearcoatRoughness,
  buttonsReflectivity: 0.5,
  // Device materials — Antennas
  antennasColor: deviceConfig.materials.antenna.color,
  antennasMetalness: deviceConfig.materials.antenna.metalness,
  antennasRoughness: deviceConfig.materials.antenna.roughness,
  antennasClearcoat: deviceConfig.materials.antenna.clearcoat,
  antennasClearcoatRoughness: deviceConfig.materials.antenna.clearcoatRoughness,
  antennasReflectivity: 0.5,
  // Device materials — Screws (clearcoat/reflectivity default when missing in legacy configs)
  screwsColor: deviceConfig.materials.screw.color,
  screwsMetalness: deviceConfig.materials.screw.metalness,
  screwsRoughness: deviceConfig.materials.screw.roughness,
  screwsClearcoat: deviceConfig.materials.screw.clearcoat ?? 0,
  screwsClearcoatRoughness: deviceConfig.materials.screw.clearcoatRoughness ?? 0,
  screwsReflectivity: deviceConfig.materials.screw.reflectivity ?? 0.5,
  // Device materials — Front speaker
  frontSpeakerColor: deviceConfig.materials.frontSpeaker.color,
  frontSpeakerMetalness: deviceConfig.materials.frontSpeaker.metalness,
  frontSpeakerRoughness: deviceConfig.materials.frontSpeaker.roughness,
  frontSpeakerClearcoat: deviceConfig.materials.frontSpeaker.clearcoat,
  frontSpeakerClearcoatRoughness: deviceConfig.materials.frontSpeaker.clearcoatRoughness,
  frontSpeakerReflectivity: deviceConfig.materials.frontSpeaker.reflectivity,
  // Device materials — Speaker grill (clearcoat/reflectivity default when missing in legacy configs)
  speakerGrillColor: deviceConfig.materials.speakerGrill.color,
  speakerGrillMetalness: deviceConfig.materials.speakerGrill.metalness,
  speakerGrillRoughness: deviceConfig.materials.speakerGrill.roughness,
  speakerGrillClearcoat: deviceConfig.materials.speakerGrill.clearcoat ?? 0,
  speakerGrillClearcoatRoughness: deviceConfig.materials.speakerGrill.clearcoatRoughness ?? 0,
  speakerGrillReflectivity: deviceConfig.materials.speakerGrill.reflectivity ?? 0.5,
  // Device materials — Charging connector tongue (legacy fallbacks match prior hardcoded look)
  chargingConnectorTongueColor: deviceConfig.materials.chargingConnectorTongue?.color ?? '#B5AFA6',
  chargingConnectorTongueMetalness: deviceConfig.materials.chargingConnectorTongue?.metalness ?? 0.55,
  chargingConnectorTongueRoughness: deviceConfig.materials.chargingConnectorTongue?.roughness ?? 0.38,
  chargingConnectorTongueClearcoat: deviceConfig.materials.chargingConnectorTongue?.clearcoat ?? 0.3,
  chargingConnectorTongueClearcoatRoughness: deviceConfig.materials.chargingConnectorTongue?.clearcoatRoughness ?? 0.4,
  chargingConnectorTongueReflectivity: deviceConfig.materials.chargingConnectorTongue?.reflectivity ?? 0.5,
  // Device materials — Charging connector rim
  chargingConnectorRimColor: deviceConfig.materials.chargingConnectorRim?.color ?? '#6e6b66',
  chargingConnectorRimMetalness: deviceConfig.materials.chargingConnectorRim?.metalness ?? 0.45,
  chargingConnectorRimRoughness: deviceConfig.materials.chargingConnectorRim?.roughness ?? 0.55,
  chargingConnectorRimClearcoat: deviceConfig.materials.chargingConnectorRim?.clearcoat ?? 0.1,
  chargingConnectorRimClearcoatRoughness: deviceConfig.materials.chargingConnectorRim?.clearcoatRoughness ?? 0.9,
  chargingConnectorRimReflectivity: deviceConfig.materials.chargingConnectorRim?.reflectivity ?? 0.5,
  // Device body surface textures
  bodyNoiseIntensity: deviceConfig.surfaceTexture?.noiseIntensity ?? 0,
  bodyNormalStrength: deviceConfig.surfaceTexture?.normalStrength ?? 0,
  bodyNormalPattern: (deviceConfig.surfaceTexture?.normalPattern as BodyNormalPattern) ?? 'brushed',
  buttonsNoiseIntensity: deviceConfig.surfaceTexture?.noiseIntensity ?? 0,
  buttonsNormalStrength: deviceConfig.surfaceTexture?.normalStrength ?? 0,
  buttonsNormalPattern: (deviceConfig.surfaceTexture?.normalPattern as BodyNormalPattern) ?? 'brushed',
})

// ── Initial state (empty — populated by EditorConfigProvider) ─────

const initialState: ScenesState = {
  activeSceneIndex: 0,
  scenes: [],
  sceneNames: [],
}

// ── Helpers ───────────────────────────────────────────────────────

let stopCounter = 2

export const generateStopId = (): string => {
  stopCounter += 1
  return `stop - ${stopCounter} `
}

/** Get the active scene from state — used internally by reducers */
const active = (state: ScenesState): SingleSceneState => state.scenes[state.activeSceneIndex]

/** Clamp all CSG front features so they stay within the front face area */
const clampFrontFeatures = (s: SingleSceneState) => {
  const gap = FRONT_FACE_GAP
  const clampedBevel = Math.min(s.deviceEdgeBevel, s.deviceWidth / 2, s.deviceHeight / 2)
  const baseW = s.deviceWidth - 2 * clampedBevel
  const baseH = s.deviceHeight - 2 * clampedBevel
  const fw = Math.max(0.01, baseW - 2 * s.frontFaceInset - 2 * gap)
  const fh = Math.max(0.01, baseH - 2 * s.frontFaceInset - 2 * gap)

  for (const f of s.frontFaceFeatures) {
    switch (f.type) {
      case 'frontSpeaker': {
        // Loosened like side-edge speakers: size bounded by the full flat front
        // (not the inset display panel), position free on both X and Y axes.
        f.width = Math.min(f.width, baseW)
        f.height = Math.min(f.height, baseH)
        const xLim = Math.max(0, s.deviceWidth / 2 - clampedBevel - f.width / 2)
        const yLim = Math.max(0, s.deviceHeight / 2 - clampedBevel - f.height / 2)
        f.xOffset = Math.max(-xLim, Math.min(xLim, f.xOffset ?? 0))
        f.yOffset = Math.max(-yLim, Math.min(yLim, f.yOffset))
        break
      }
      case 'homeButton': {
        f.radius = Math.min(f.radius, fw / 2, fh / 2)
        const limX = Math.max(0, s.deviceWidth / 2 - clampedBevel - f.radius)
        const limY = Math.max(0, s.deviceHeight / 2 - clampedBevel - f.radius)
        f.x = Math.max(-limX, Math.min(limX, f.x))
        f.y = Math.max(-limY, Math.min(limY, f.y))
        break
      }
      case 'cameraLens': {
        f.radius = Math.min(f.radius, fw / 2, fh / 2)
        // Position spans the full flat front (out to the edge bevel), not just the panel.
        const lensLimX = Math.max(0, s.deviceWidth / 2 - clampedBevel - f.radius)
        const lensLimY = Math.max(0, s.deviceHeight / 2 - clampedBevel - f.radius)
        f.xOffset = Math.max(-lensLimX, Math.min(lensLimX, f.xOffset))
        f.yOffset = Math.max(-lensLimY, Math.min(lensLimY, f.yOffset))
        break
      }
      case 'displayCutout': {
        f.width = Math.min(f.width, fw)
        f.height = Math.min(f.height, fh)
        f.cornerRadius = Math.min(f.cornerRadius, Math.min(f.width, f.height) / 2)
        const cutLimX = Math.max(0, s.deviceWidth / 2 - clampedBevel - f.width / 2)
        const cutLimY = Math.max(0, s.deviceHeight / 2 - clampedBevel - f.height / 2)
        f.xOffset = Math.max(-cutLimX, Math.min(cutLimX, f.xOffset))
        f.yOffset = Math.max(-cutLimY, Math.min(cutLimY, f.yOffset))
        break
      }
      case 'homeIndicator': {
        f.width = Math.min(f.width, fw)
        f.height = Math.min(f.height, fh)
        f.cornerRadius = Math.min(f.cornerRadius, Math.min(f.width, f.height) / 2)
        const indLimY = Math.max(0, s.deviceHeight / 2 - clampedBevel - f.height / 2)
        f.yOffset = Math.max(-indLimY, Math.min(indLimY, f.yOffset))
        break
      }
    }
  }
}

/** Minimum feature size (mm) — features below this are too small for CSG */
const MIN_FEATURE_SIZE = 0.3

/** Clamp all side-edge features so they stay within valid edge bounds */
const clampSideEdgeFeatures = (s: SingleSceneState) => {
  const edges: SideEdge[] = ['left', 'right', 'top', 'bottom']
  for (const edge of edges) {
    const isHoriz = edge === 'left' || edge === 'right'
    const edgeLen = isHoriz ? s.deviceHeight : s.deviceWidth
    const cr = s.deviceCornerRadius
    const flatEdge = Math.max(MIN_FEATURE_SIZE, edgeLen - 2 * cr)

    for (const f of s.sideEdgeFeatures[edge]) {
      switch (f.type) {
        case 'sideButton':
        case 'muteSwitch': {
          f.height = Math.min(f.height, flatEdge)
          f.width = Math.min(f.width, Math.max(0.5, s.deviceDepth))
          f.cornerRadius = Math.min(f.cornerRadius, Math.min(f.width, f.height) / 2)
          const hbPos = Math.max(0, edgeLen / 2 - cr - f.height / 2)
          f.position = Math.max(-hbPos, Math.min(hbPos, f.position))
          break
        }
        case 'antenna': {
          const haPos = Math.max(0, edgeLen / 2 - cr - f.width / 2)
          f.position = Math.max(-haPos, Math.min(haPos, f.position))
          break
        }
        case 'screw': {
          f.diameter = Math.min(f.diameter, Math.max(0.5, s.deviceDepth))
          const hsPos = Math.max(0, edgeLen / 2 - cr - f.diameter / 2)
          f.position = Math.max(-hsPos, Math.min(hsPos, f.position))
          break
        }
        case 'sideSpeakerGrill': {
          // height = along-edge (bounded by flat edge), width = across/into-device (bounded by depth)
          f.height = Math.min(f.height, flatEdge)
          f.width = Math.min(f.width, Math.max(0.3, s.deviceDepth))
          f.cornerRadius = Math.min(f.cornerRadius, Math.min(f.width, f.height) / 2)
          const hgPos = Math.max(0, edgeLen / 2 - cr - f.height / 2)
          f.position = Math.max(-hgPos, Math.min(hgPos, f.position))
          break
        }
        case 'chargingConnector': {
          f.width = Math.min(f.width, flatEdge)
          f.height = Math.min(f.height, Math.max(0.5, s.deviceDepth))
          f.cornerRadius = Math.min(f.cornerRadius, Math.min(f.width, f.height) / 2)
          const hcPos = Math.max(0, edgeLen / 2 - cr - f.width / 2)
          f.position = Math.max(-hcPos, Math.min(hcPos, f.position))
          break
        }
        case 'simCardSlot': {
          f.height = Math.min(f.height, flatEdge)
          f.width = Math.min(f.width, Math.max(0.5, s.deviceDepth))
          f.cornerRadius = Math.min(f.cornerRadius, Math.min(f.width, f.height) / 2)
          const pinMax = Math.max(0, f.height / 2 - 1)
          f.pinHoleOffset = Math.max(-pinMax, Math.min(pinMax, f.pinHoleOffset))
          const hsimPos = Math.max(0, edgeLen / 2 - cr - f.height / 2)
          f.position = Math.max(-hsimPos, Math.min(hsimPos, f.position))
          break
        }
      }
    }
  }
}

/**
 * Clamp custom decals so their offsets keep the plane within the flat surface —
 * on the front face up to the edge bevel, on side edges up to where the corner
 * curve / edge thickness begins. Mirrors the cameraLens / side-button rules.
 */
const clampCustomDecals = (s: SingleSceneState) => {
  const cr = s.deviceCornerRadius
  const clampedBevel = Math.min(s.deviceEdgeBevel, s.deviceWidth / 2, s.deviceHeight / 2, s.deviceDepth / 2)
  for (const d of s.customDecals) {
    // Surface extents along the U / V axes for this placement.
    let su: number
    let sv: number
    if (d.placement === 'front') {
      su = Math.max(MIN_CUSTOM_DECAL_SIZE, s.deviceWidth - 2 * clampedBevel)
      sv = Math.max(MIN_CUSTOM_DECAL_SIZE, s.deviceHeight - 2 * clampedBevel)
    } else if (d.placement === 'left' || d.placement === 'right') {
      su = Math.max(MIN_CUSTOM_DECAL_SIZE, s.deviceDepth)
      sv = Math.max(MIN_CUSTOM_DECAL_SIZE, s.deviceHeight - 2 * cr)
    } else {
      su = Math.max(MIN_CUSTOM_DECAL_SIZE, s.deviceWidth - 2 * cr)
      sv = Math.max(MIN_CUSTOM_DECAL_SIZE, s.deviceDepth)
    }
    // Size must keep the plane inside the surface, then offsets keep it on the flat region.
    d.size = Math.max(MIN_CUSTOM_DECAL_SIZE, Math.min(d.size, maxCustomDecalSize(d.imageAspect, su, sv)))
    const [planeW, planeH] = customDecalPlaneSize(d)
    const halfU = planeW / 2
    const halfV = planeH / 2
    let limU: number
    let limV: number
    if (d.placement === 'front') {
      limU = Math.max(0, s.deviceWidth / 2 - clampedBevel - halfU)
      limV = Math.max(0, s.deviceHeight / 2 - clampedBevel - halfV)
    } else if (d.placement === 'left' || d.placement === 'right') {
      // U = depth, V = along the height edge
      limU = Math.max(0, s.deviceDepth / 2 - halfU)
      limV = Math.max(0, s.deviceHeight / 2 - cr - halfV)
    } else {
      // top / bottom: U = along the width edge, V = depth
      limU = Math.max(0, s.deviceWidth / 2 - cr - halfU)
      limV = Math.max(0, s.deviceDepth / 2 - halfV)
    }
    d.offsetU = Math.max(-limU, Math.min(limU, d.offsetU))
    d.offsetV = Math.max(-limV, Math.min(limV, d.offsetV))
  }
}

// ── Slice ─────────────────────────────────────────────────────────

const scenesSlice = createSlice({
  name: 'scenes',
  initialState,
  reducers: {
    // ── Initialization ──────────────────────────────────────────
    initializeWithDevice: (state, action: PayloadAction<DeviceConfig>) => {
      state.scenes = [createDefaultScene(action.payload)]
      state.sceneNames = ['Scene 1']
      state.activeSceneIndex = 0
    },
    /** Load a saved project — replaces all scenes with deserialized configs */
    loadProject: (
      state,
      action: PayloadAction<{scenes: SceneFullConfig[]; deviceConfig: DeviceConfig; sceneNames?: string[]}>,
    ) => {
      const {scenes, deviceConfig, sceneNames} = action.payload
      state.scenes = scenes.map((cfg) => {
        const base = createDefaultScene(deviceConfig)
        // Scene-level overrides
        if (cfg.background) {
          base.bgMode = cfg.background.mode ?? 'gradient'
          base.bgGradientStops = cfg.background.gradientStops ?? [
            {id: 'stop-0', color: '#ffffff', position: 0},
            {id: 'stop-1', color: '#e0e7ff', position: 1},
          ]
          base.bgAngle = cfg.background.angle ?? 0
          if (cfg.background.imageKey) base.bgImageKey = cfg.background.imageKey
          if (cfg.background.imageUrl) base.bgImageUrl = cfg.background.imageUrl
          if (cfg.background.imageFit) base.bgImageFit = cfg.background.imageFit
          if (cfg.background.imageOffsetX !== undefined) base.bgImageOffsetX = cfg.background.imageOffsetX
          if (cfg.background.imageOffsetY !== undefined) base.bgImageOffsetY = cfg.background.imageOffsetY
          if (cfg.background.imageZoom !== undefined) base.bgImageZoom = cfg.background.imageZoom
        }
        if (cfg.camera) {
          base.cameraPosition = cfg.camera.position as number[]
          base.cameraRotation = (cfg.camera.rotation ?? [0, 0, 0]) as number[]
        }
        if (cfg.lighting) {
          base.ambientIntensity = cfg.lighting.ambient.intensity
          base.ambientColor = cfg.lighting.ambient.color
          base.lights = cfg.lighting.lights as SceneLightConfig[]
        }
        if (cfg.environment) {
          base.envPreset = cfg.environment.preset ?? 'city'
          base.envMapIntensity = cfg.environment.envMapIntensity
        }
        if (cfg.ground) {
          base.groundDistance = cfg.ground.offset
        }
        if (cfg.textureKey) {
          base.screenImageKey = cfg.textureKey
        }
        if (cfg.textureUrl) {
          base.screenImageUrl = cfg.textureUrl
        }
        if (cfg.export) {
          base.exportWidth = clampExportDim(cfg.export.width)
          base.exportHeight = clampExportDim(cfg.export.height)
          base.exportCarouselEnabled = cfg.export.carousel ?? false
          base.exportCarouselCount = clampCarouselCount(cfg.export.carouselCount ?? DEFAULT_CAROUSEL_COUNT)
          base.exportCarouselGap = clampCarouselGap(cfg.export.carouselGap ?? DEFAULT_CAROUSEL_GAP_PCT)
        }
        if (typeof cfg.deviceRotationZ === 'number') {
          base.deviceRotationZ = ((cfg.deviceRotationZ % 360) + 360) % 360
        }
        if (typeof cfg.devicePositionZ === 'number') {
          base.devicePositionZ = clampDevicePositionZ(cfg.devicePositionZ)
        }
        if (typeof cfg.displayReflection === 'number') {
          base.displayReflection = Math.max(0, Math.min(1, cfg.displayReflection))
        }
        if (cfg.text2d) {
          base.texts = cfg.text2d as ScreenText[]
        }
        if (cfg.text3d) {
          base.texts3d = JSON.parse(JSON.stringify(cfg.text3d)) as SceneText3D[]
        }
        if (cfg.sprites2d) {
          base.sprites2d = cfg.sprites2d as ScreenSprite[]
        }
        if (cfg.sprites3d) {
          base.sprites3d = JSON.parse(JSON.stringify(cfg.sprites3d)) as SceneSprite3D[]
        }
        if (cfg.customDecals) {
          base.customDecals = JSON.parse(JSON.stringify(cfg.customDecals)) as CustomDecal[]
        }
        // Device overrides from saved config
        if (cfg.device) {
          const d = cfg.device
          base.activeDeviceName = d.name
          base.deviceWidth = d.body.width
          base.deviceHeight = d.body.height
          base.deviceDepth = d.body.depth
          base.deviceCornerRadius = d.body.cornerRadius
          base.deviceEdgeBevel = d.body.edgeBevel
          base.frontFaceInset = d.frontFace.inset
          base.displayCornerRadius = d.display.cornerRadius
          base.displayInsetX = d.display.insetX
          base.displayInsetY = d.display.insetY
          base.frontFaceFeatures = d.frontFaceFeatures
          base.sideEdgeFeatures = d.sideEdgeFeatures
          if (d.materials?.body) {
            base.bodyColor = d.materials.body.color
            base.bodyMetalness = d.materials.body.metalness
            base.bodyRoughness = d.materials.body.roughness
            base.bodyClearcoat = d.materials.body.clearcoat
            base.bodyClearcoatRoughness = d.materials.body.clearcoatRoughness
          }
          if (d.materials?.frontFace) {
            base.frontFaceColor = d.materials.frontFace.color
            base.frontFaceMetalness = d.materials.frontFace.metalness
            base.frontFaceRoughness = d.materials.frontFace.roughness
            base.frontFaceClearcoat = d.materials.frontFace.clearcoat
            base.frontFaceClearcoatRoughness = d.materials.frontFace.clearcoatRoughness
            base.frontFaceReflectivity = d.materials.frontFace.reflectivity
          }
          if (d.materials?.buttons) {
            base.buttonsColor = d.materials.buttons.color
            base.buttonsMetalness = d.materials.buttons.metalness
            base.buttonsRoughness = d.materials.buttons.roughness
            base.buttonsClearcoat = d.materials.buttons.clearcoat
            base.buttonsClearcoatRoughness = d.materials.buttons.clearcoatRoughness
            base.buttonsReflectivity = d.materials.buttons.reflectivity
          }
          if (d.materials?.antenna) {
            base.antennasColor = d.materials.antenna.color
            base.antennasMetalness = d.materials.antenna.metalness
            base.antennasRoughness = d.materials.antenna.roughness
            base.antennasClearcoat = d.materials.antenna.clearcoat
            base.antennasClearcoatRoughness = d.materials.antenna.clearcoatRoughness
          }
          if (d.materials?.screw) {
            base.screwsColor = d.materials.screw.color
            base.screwsMetalness = d.materials.screw.metalness
            base.screwsRoughness = d.materials.screw.roughness
            if (d.materials.screw.clearcoat !== undefined) base.screwsClearcoat = d.materials.screw.clearcoat
            if (d.materials.screw.clearcoatRoughness !== undefined) {
              base.screwsClearcoatRoughness = d.materials.screw.clearcoatRoughness
            }
            if (d.materials.screw.reflectivity !== undefined) {
              base.screwsReflectivity = d.materials.screw.reflectivity
            }
          }
          if (d.materials?.frontSpeaker) {
            base.frontSpeakerColor = d.materials.frontSpeaker.color
            base.frontSpeakerMetalness = d.materials.frontSpeaker.metalness
            base.frontSpeakerRoughness = d.materials.frontSpeaker.roughness
            base.frontSpeakerClearcoat = d.materials.frontSpeaker.clearcoat
            base.frontSpeakerClearcoatRoughness = d.materials.frontSpeaker.clearcoatRoughness
            base.frontSpeakerReflectivity = d.materials.frontSpeaker.reflectivity
          }
          if (d.materials?.speakerGrill) {
            base.speakerGrillColor = d.materials.speakerGrill.color
            base.speakerGrillMetalness = d.materials.speakerGrill.metalness
            base.speakerGrillRoughness = d.materials.speakerGrill.roughness
            if (d.materials.speakerGrill.clearcoat !== undefined) {
              base.speakerGrillClearcoat = d.materials.speakerGrill.clearcoat
            }
            if (d.materials.speakerGrill.clearcoatRoughness !== undefined) {
              base.speakerGrillClearcoatRoughness = d.materials.speakerGrill.clearcoatRoughness
            }
            if (d.materials.speakerGrill.reflectivity !== undefined) {
              base.speakerGrillReflectivity = d.materials.speakerGrill.reflectivity
            }
          }
          if (d.materials?.chargingConnectorTongue) {
            const m = d.materials.chargingConnectorTongue
            base.chargingConnectorTongueColor = m.color
            base.chargingConnectorTongueMetalness = m.metalness
            base.chargingConnectorTongueRoughness = m.roughness
            base.chargingConnectorTongueClearcoat = m.clearcoat
            base.chargingConnectorTongueClearcoatRoughness = m.clearcoatRoughness
            base.chargingConnectorTongueReflectivity = m.reflectivity
          }
          if (d.materials?.chargingConnectorRim) {
            const m = d.materials.chargingConnectorRim
            base.chargingConnectorRimColor = m.color
            base.chargingConnectorRimMetalness = m.metalness
            base.chargingConnectorRimRoughness = m.roughness
            base.chargingConnectorRimClearcoat = m.clearcoat
            base.chargingConnectorRimClearcoatRoughness = m.clearcoatRoughness
            base.chargingConnectorRimReflectivity = m.reflectivity
          }
          if (d.surfaceTexture) {
            base.bodyNoiseIntensity = d.surfaceTexture.noiseIntensity
            base.bodyNormalStrength = d.surfaceTexture.normalStrength
            base.bodyNormalPattern = d.surfaceTexture.normalPattern as BodyNormalPattern
          }
          if (d.buttonsSurfaceTexture) {
            base.buttonsNoiseIntensity = d.buttonsSurfaceTexture.noiseIntensity
            base.buttonsNormalStrength = d.buttonsSurfaceTexture.normalStrength
            base.buttonsNormalPattern = d.buttonsSurfaceTexture.normalPattern as BodyNormalPattern
          }
        }
        return base
      })
      state.sceneNames = sceneNames ?? scenes.map((_, i) => `Scene ${i + 1}`)
      state.activeSceneIndex = 0
    },

    // ── Scene management ────────────────────────────────────────
    setActiveScene: (state, action: PayloadAction<number>) => {
      if (action.payload >= 0 && action.payload < state.scenes.length) {
        state.activeSceneIndex = action.payload
      }
    },
    addScene: (state, action: PayloadAction<string | undefined>) => {
      // Deep-copy active scene
      const clone = JSON.parse(JSON.stringify(active(state))) as SingleSceneState
      state.scenes.push(clone)
      state.sceneNames.push(action.payload ?? `Scene ${state.scenes.length}`)
      state.activeSceneIndex = state.scenes.length - 1
    },
    addNewScene: (state, action: PayloadAction<{deviceConfig: DeviceConfig; name?: string}>) => {
      // Create a fresh scene with the provided device config
      state.scenes.push(createDefaultScene(action.payload.deviceConfig))
      state.sceneNames.push(action.payload.name ?? `Scene ${state.scenes.length}`)
      state.activeSceneIndex = state.scenes.length - 1
    },
    removeScene: (state, action: PayloadAction<number>) => {
      if (state.scenes.length <= 1) return
      state.scenes.splice(action.payload, 1)
      state.sceneNames.splice(action.payload, 1)
      if (state.activeSceneIndex >= state.scenes.length) {
        state.activeSceneIndex = state.scenes.length - 1
      }
    },
    renameScene: (state, action: PayloadAction<{index: number; name: string}>) => {
      if (action.payload.index >= 0 && action.payload.index < state.sceneNames.length) {
        state.sceneNames[action.payload.index] = action.payload.name
      }
    },

    // ── Background reducers ─────────────────────────────────────
    setBgMode: (state, action: PayloadAction<BgMode>) => {
      active(state).bgMode = action.payload
      // Clear image fields when switching away from image mode so S3 assets get cleaned up on save
      if (action.payload !== 'image') {
        active(state).bgImageKey = ''
        active(state).bgImageUrl = ''
      }
    },
    addGradientStop: (state, action: PayloadAction<{id: string; color: string; position: number}>) => {
      const s = active(state)
      const {id, color, position} = action.payload
      s.bgGradientStops.push({id, color, position})
      s.bgGradientStops.sort((a, b) => a.position - b.position)
    },
    removeGradientStop: (state, action: PayloadAction<string>) => {
      const s = active(state)
      if (s.bgGradientStops.length <= 2) return
      s.bgGradientStops = s.bgGradientStops.filter((stop) => stop.id !== action.payload)
    },
    updateGradientStop: (state, action: PayloadAction<{id: string; changes: Partial<GradientStop>}>) => {
      const s = active(state)
      const stop = s.bgGradientStops.find((st) => st.id === action.payload.id)
      if (stop) {
        Object.assign(stop, action.payload.changes)
        s.bgGradientStops.sort((a, b) => a.position - b.position)
      }
    },
    setBgAngle: (state, action: PayloadAction<number>) => {
      active(state).bgAngle = action.payload
    },
    setBgImageKey: (state, action: PayloadAction<string>) => {
      active(state).bgImageKey = action.payload
    },
    setBgImageUrl: (state, action: PayloadAction<string>) => {
      active(state).bgImageUrl = action.payload
      // Clear S3 key when replaced with a local data URL so save re-uploads
      if (action.payload.startsWith('data:')) {
        active(state).bgImageKey = ''
      }
    },
    setBgImageUploaded: (state, action: PayloadAction<{index: number; key: string; url: string}>) => {
      const scene = state.scenes[action.payload.index]
      if (scene) {
        scene.bgImageKey = action.payload.key
        scene.bgImageUrl = action.payload.url
      }
    },
    setBgImageFit: (state, action: PayloadAction<BgImageFit>) => {
      active(state).bgImageFit = action.payload
    },
    setBgImageOffset: (state, action: PayloadAction<{x: number; y: number}>) => {
      active(state).bgImageOffsetX = action.payload.x
      active(state).bgImageOffsetY = action.payload.y
    },
    setBgImageZoom: (state, action: PayloadAction<number>) => {
      active(state).bgImageZoom = action.payload
    },

    // ── Lighting reducers ───────────────────────────────────────
    updateLight: (state, action: PayloadAction<{id: string; changes: Partial<SceneLightConfig>}>) => {
      const light = active(state).lights.find((l) => l.id === action.payload.id)
      if (light) Object.assign(light, action.payload.changes)
    },
    setAmbientIntensity: (state, action: PayloadAction<number>) => {
      active(state).ambientIntensity = action.payload
    },
    setAmbientColor: (state, action: PayloadAction<string>) => {
      active(state).ambientColor = action.payload
    },

    // ── Environment reducers ────────────────────────────────────
    setEnvPreset: (state, action: PayloadAction<string>) => {
      active(state).envPreset = action.payload
    },
    setEnvMapIntensity: (state, action: PayloadAction<number>) => {
      active(state).envMapIntensity = action.payload
    },

    // ── Ground reducers ─────────────────────────────────────────
    setGroundDistance: (state, action: PayloadAction<number>) => {
      active(state).groundDistance = action.payload
    },

    // ── Screen image reducers ───────────────────────────────────
    setScreenImageUrl: (state, action: PayloadAction<string>) => {
      active(state).screenImageUrl = action.payload
      // Clear S3 key when replaced with a local data URL so save re-uploads
      if (action.payload.startsWith('data:')) {
        active(state).screenImageKey = ''
      }
    },
    setScreenImageUploaded: (state, action: PayloadAction<{index: number; key: string; url: string}>) => {
      const scene = state.scenes[action.payload.index]
      if (scene) {
        scene.screenImageKey = action.payload.key
        scene.screenImageUrl = action.payload.url
      }
    },

    // ── Export reducers ─────────────────────────────────────────
    setExportWidth: (state, action: PayloadAction<number>) => {
      active(state).exportWidth = clampExportDim(action.payload)
    },
    setExportHeight: (state, action: PayloadAction<number>) => {
      active(state).exportHeight = clampExportDim(action.payload)
    },
    setExportSize: (state, action: PayloadAction<{width: number; height: number}>) => {
      active(state).exportWidth = clampExportDim(action.payload.width)
      active(state).exportHeight = clampExportDim(action.payload.height)
    },
    setExportFrameEnabled: (state, action: PayloadAction<boolean>) => {
      active(state).exportFrameEnabled = action.payload
    },
    setExportCarouselEnabled: (state, action: PayloadAction<boolean>) => {
      active(state).exportCarouselEnabled = action.payload
    },
    setExportCarouselCount: (state, action: PayloadAction<number>) => {
      active(state).exportCarouselCount = clampCarouselCount(action.payload)
    },
    setExportCarouselGap: (state, action: PayloadAction<number>) => {
      active(state).exportCarouselGap = clampCarouselGap(action.payload)
    },
    setDeviceRotationZ: (state, action: PayloadAction<number>) => {
      active(state).deviceRotationZ = ((action.payload % 360) + 360) % 360
    },
    setDevicePositionZ: (state, action: PayloadAction<number>) => {
      active(state).devicePositionZ = clampDevicePositionZ(action.payload)
    },

    // ── 2D Text reducers ────────────────────────────────────────
    addText: (state) => {
      active(state).texts.push(createScreenText())
    },
    removeText: (state, action: PayloadAction<string>) => {
      const s = active(state)
      s.texts = s.texts.filter((t) => t.id !== action.payload)
    },
    updateText: (state, action: PayloadAction<{id: string; changes: Partial<ScreenText>}>) => {
      const text = active(state).texts.find((t) => t.id === action.payload.id)
      if (text) Object.assign(text, action.payload.changes)
    },

    // ── 3D Text reducers ────────────────────────────────────────
    addText3D: (state) => {
      active(state).texts3d.push(createSceneText3D())
    },
    removeText3D: (state, action: PayloadAction<string>) => {
      const s = active(state)
      s.texts3d = s.texts3d.filter((t) => t.id !== action.payload)
    },
    updateText3D: (state, action: PayloadAction<{id: string; changes: Partial<SceneText3D>}>) => {
      const text = active(state).texts3d.find((t) => t.id === action.payload.id)
      if (text) Object.assign(text, action.payload.changes)
    },

    // ── 2D Sprite reducers ──────────────────────────────────────
    addSprite2D: (state) => {
      active(state).sprites2d.push(createScreenSprite())
    },
    removeSprite2D: (state, action: PayloadAction<string>) => {
      const s = active(state)
      s.sprites2d = s.sprites2d.filter((sp) => sp.id !== action.payload)
    },
    updateSprite2D: (state, action: PayloadAction<{id: string; changes: Partial<ScreenSprite>}>) => {
      const sprite = active(state).sprites2d.find((sp) => sp.id === action.payload.id)
      if (sprite) Object.assign(sprite, action.payload.changes)
    },

    // ── 3D Sprite reducers ──────────────────────────────────────
    addSprite3D: (state) => {
      active(state).sprites3d.push(createSceneSprite3D())
    },
    removeSprite3D: (state, action: PayloadAction<string>) => {
      const s = active(state)
      s.sprites3d = s.sprites3d.filter((sp) => sp.id !== action.payload)
    },
    updateSprite3D: (state, action: PayloadAction<{id: string; changes: Partial<SceneSprite3D>}>) => {
      const sprite = active(state).sprites3d.find((sp) => sp.id === action.payload.id)
      if (sprite) Object.assign(sprite, action.payload.changes)
    },
    updateSprite3DMaterial: (state, action: PayloadAction<{id: string; changes: Partial<Sprite3DMaterial>}>) => {
      const sprite = active(state).sprites3d.find((sp) => sp.id === action.payload.id)
      if (sprite) Object.assign(sprite.material, action.payload.changes)
    },

    // ── Custom decal reducers ───────────────────────────────────
    // Editing decals diverges from the preset → mark the device "Custom" (mirrors the
    // device-dimension reducers). setCustomDecalUploaded (save-time key swap) is excluded.
    addCustomDecal: (state, action: PayloadAction<{placement: CustomDecalPlacement; id?: string}>) => {
      const s = active(state)
      s.activeDeviceName = CUSTOM_DEVICE_NAME
      s.customDecals.push(createCustomDecal(action.payload.placement, action.payload.id))
      clampCustomDecals(s)
    },
    removeCustomDecal: (state, action: PayloadAction<string>) => {
      const s = active(state)
      s.activeDeviceName = CUSTOM_DEVICE_NAME
      s.customDecals = s.customDecals.filter((d) => d.id !== action.payload)
    },
    updateCustomDecal: (state, action: PayloadAction<{id: string; changes: Partial<CustomDecal>}>) => {
      const s = active(state)
      const decal = s.customDecals.find((d) => d.id === action.payload.id)
      if (decal) {
        s.activeDeviceName = CUSTOM_DEVICE_NAME
        Object.assign(decal, action.payload.changes)
        clampCustomDecals(s)
      }
    },
    updateCustomDecalMaterial: (state, action: PayloadAction<{id: string; changes: Partial<Sprite3DMaterial>}>) => {
      const s = active(state)
      const decal = s.customDecals.find((d) => d.id === action.payload.id)
      if (decal) {
        s.activeDeviceName = CUSTOM_DEVICE_NAME
        Object.assign(decal.material, action.payload.changes)
      }
    },
    /** Set the S3 key + URL of a decal in a specific scene (used during save to swap data URLs). */
    setCustomDecalUploaded: (
      state,
      action: PayloadAction<{sceneIndex: number; id: string; key: string; url: string}>,
    ) => {
      const scene = state.scenes[action.payload.sceneIndex]
      const decal = scene?.customDecals.find((d) => d.id === action.payload.id)
      if (decal) {
        decal.imageKey = action.payload.key
        decal.imageUrl = action.payload.url
      }
    },
    /** Set the S3 key + URL of a 2D sprite in a specific scene (used during save to swap data URLs). */
    setSprite2DUploaded: (state, action: PayloadAction<{sceneIndex: number; id: string; key: string; url: string}>) => {
      const sprite = state.scenes[action.payload.sceneIndex]?.sprites2d.find((sp) => sp.id === action.payload.id)
      if (sprite) {
        sprite.imageKey = action.payload.key
        sprite.imageUrl = action.payload.url
      }
    },
    /** Set the S3 key + URL of a 3D sprite in a specific scene (used during save to swap data URLs). */
    setSprite3DUploaded: (state, action: PayloadAction<{sceneIndex: number; id: string; key: string; url: string}>) => {
      const sprite = state.scenes[action.payload.sceneIndex]?.sprites3d.find((sp) => sp.id === action.payload.id)
      if (sprite) {
        sprite.imageKey = action.payload.key
        sprite.imageUrl = action.payload.url
      }
    },

    // ── Camera reducers ─────────────────────────────────────────
    setCameraPosition: (state, action: PayloadAction<{position: number[]; rotation: number[]}>) => {
      active(state).cameraPosition = action.payload.position
      active(state).cameraRotation = action.payload.rotation
    },
    /** Write camera state to a specific scene (used when saving old scene before switching) */
    setCameraPositionForScene: (
      state,
      action: PayloadAction<{
        sceneIndex: number
        position: number[]
        rotation: number[]
      }>,
    ) => {
      const {sceneIndex, position, rotation} = action.payload
      if (sceneIndex >= 0 && sceneIndex < state.scenes.length) {
        state.scenes[sceneIndex].cameraPosition = position
        state.scenes[sceneIndex].cameraRotation = rotation
      }
    },
    // ── Device dimension reducers ───────────────────────────────
    setDeviceWidth: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).deviceWidth = action.payload
      clampFrontFeatures(active(state))
      clampSideEdgeFeatures(active(state))
      clampCustomDecals(active(state))
    },
    setDeviceHeight: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).deviceHeight = action.payload
      clampFrontFeatures(active(state))
      clampSideEdgeFeatures(active(state))
      clampCustomDecals(active(state))
    },
    setDeviceDepth: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).deviceDepth = action.payload
      clampSideEdgeFeatures(active(state))
      clampCustomDecals(active(state))
    },
    setDeviceCornerRadius: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).deviceCornerRadius = action.payload
      clampFrontFeatures(active(state))
      clampSideEdgeFeatures(active(state))
      clampCustomDecals(active(state))
    },
    setDeviceEdgeBevel: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).deviceEdgeBevel = action.payload
      clampFrontFeatures(active(state))
      clampSideEdgeFeatures(active(state))
      clampCustomDecals(active(state))
    },
    setFrontFaceInset: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontFaceInset = action.payload
      clampFrontFeatures(active(state))
      clampCustomDecals(active(state))
    },
    setDisplayCornerRadius: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).displayCornerRadius = action.payload
    },
    setDisplayInsetX: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).displayInsetX = action.payload
    },
    setDisplayInsetY: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).displayInsetY = action.payload
    },
    setDisplayReflection: (state, action: PayloadAction<number>) => {
      active(state).displayReflection = Math.max(0, Math.min(1, action.payload))
    },

    // ── Front face features — array-based CRUD ──────────────────
    addFrontFaceFeature: (state, action: PayloadAction<FrontFaceFeatureConfig>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontFaceFeatures.push(action.payload)
    },
    removeFrontFaceFeature: (state, action: PayloadAction<string>) => {
      const s = active(state)
      s.activeDeviceName = CUSTOM_DEVICE_NAME
      s.frontFaceFeatures = s.frontFaceFeatures.filter((f) => f.id !== action.payload)
    },
    updateFrontFaceFeature: (state, action: PayloadAction<{id: string; changes: Partial<FrontFaceFeatureConfig>}>) => {
      const s = active(state)
      s.activeDeviceName = CUSTOM_DEVICE_NAME
      const idx = s.frontFaceFeatures.findIndex((f) => f.id === action.payload.id)
      if (idx !== -1) {
        s.frontFaceFeatures[idx] = {
          ...s.frontFaceFeatures[idx],
          ...action.payload.changes,
        } as FrontFaceFeatureConfig
      }
    },

    // ── Side edge features — per-edge CRUD ──────────────────────
    addSideEdgeFeature: (state, action: PayloadAction<{edge: SideEdge; feature: SideEdgeFeatureConfig}>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).sideEdgeFeatures[action.payload.edge].push(action.payload.feature)
    },
    removeSideEdgeFeature: (state, action: PayloadAction<{edge: SideEdge; id: string}>) => {
      const s = active(state)
      s.activeDeviceName = CUSTOM_DEVICE_NAME
      const {edge, id} = action.payload
      s.sideEdgeFeatures[edge] = s.sideEdgeFeatures[edge].filter((f) => f.id !== id)
    },
    updateSideEdgeFeature: (
      state,
      action: PayloadAction<{edge: SideEdge; id: string; changes: Partial<SideEdgeFeatureConfig>}>,
    ) => {
      const s = active(state)
      s.activeDeviceName = CUSTOM_DEVICE_NAME
      const {edge, id, changes} = action.payload
      const arr = s.sideEdgeFeatures[edge]
      const idx = arr.findIndex((f) => f.id === id)
      if (idx !== -1) {
        const updated = {...arr[idx], ...changes} as SideEdgeFeatureConfig
        // Auto-clamp position so the feature stays within the valid edge range
        updated.position = clampSideFeaturePosition(updated, edge, s.deviceWidth, s.deviceHeight, s.deviceCornerRadius)
        arr[idx] = updated
      }
    },

    // ── Device materials — Body ─────────────────────────────────
    setBodyColor: (state, action: PayloadAction<string>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).bodyColor = action.payload
    },
    setBodyMetalness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).bodyMetalness = action.payload
    },
    setBodyRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).bodyRoughness = action.payload
    },
    setBodyClearcoat: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).bodyClearcoat = action.payload
    },
    setBodyClearcoatRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).bodyClearcoatRoughness = action.payload
    },
    setBodyReflectivity: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).bodyReflectivity = action.payload
    },

    // ── Device materials — Front face ───────────────────────────
    setFrontFaceColor: (state, action: PayloadAction<string>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontFaceColor = action.payload
    },
    setFrontFaceMetalness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontFaceMetalness = action.payload
    },
    setFrontFaceRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontFaceRoughness = action.payload
    },
    setFrontFaceClearcoat: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontFaceClearcoat = action.payload
    },
    setFrontFaceClearcoatRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontFaceClearcoatRoughness = action.payload
    },
    setFrontFaceReflectivity: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontFaceReflectivity = action.payload
    },

    // ── Device materials — Buttons ──────────────────────────────
    setButtonsColor: (state, action: PayloadAction<string>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).buttonsColor = action.payload
    },
    setButtonsMetalness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).buttonsMetalness = action.payload
    },
    setButtonsRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).buttonsRoughness = action.payload
    },
    setButtonsClearcoat: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).buttonsClearcoat = action.payload
    },
    setButtonsClearcoatRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).buttonsClearcoatRoughness = action.payload
    },
    setButtonsReflectivity: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).buttonsReflectivity = action.payload
    },

    // ── Device materials — Antennas ─────────────────────────────
    setAntennasColor: (state, action: PayloadAction<string>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).antennasColor = action.payload
    },
    setAntennasMetalness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).antennasMetalness = action.payload
    },
    setAntennasRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).antennasRoughness = action.payload
    },
    setAntennasClearcoat: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).antennasClearcoat = action.payload
    },
    setAntennasClearcoatRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).antennasClearcoatRoughness = action.payload
    },
    setAntennasReflectivity: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).antennasReflectivity = action.payload
    },

    // ── Device materials — Screws ───────────────────────────────
    setScrewsColor: (state, action: PayloadAction<string>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).screwsColor = action.payload
    },
    setScrewsMetalness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).screwsMetalness = action.payload
    },
    setScrewsRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).screwsRoughness = action.payload
    },
    setScrewsClearcoat: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).screwsClearcoat = action.payload
    },
    setScrewsClearcoatRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).screwsClearcoatRoughness = action.payload
    },
    setScrewsReflectivity: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).screwsReflectivity = action.payload
    },

    // ── Device materials — Front speaker ────────────────────────
    setFrontSpeakerColor: (state, action: PayloadAction<string>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontSpeakerColor = action.payload
    },
    setFrontSpeakerMetalness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontSpeakerMetalness = action.payload
    },
    setFrontSpeakerRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontSpeakerRoughness = action.payload
    },
    setFrontSpeakerClearcoat: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontSpeakerClearcoat = action.payload
    },
    setFrontSpeakerClearcoatRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontSpeakerClearcoatRoughness = action.payload
    },
    setFrontSpeakerReflectivity: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).frontSpeakerReflectivity = action.payload
    },

    // ── Device materials — Speaker grill ────────────────────────
    setSpeakerGrillColor: (state, action: PayloadAction<string>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).speakerGrillColor = action.payload
    },
    setSpeakerGrillMetalness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).speakerGrillMetalness = action.payload
    },
    setSpeakerGrillRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).speakerGrillRoughness = action.payload
    },
    setSpeakerGrillClearcoat: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).speakerGrillClearcoat = action.payload
    },
    setSpeakerGrillClearcoatRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).speakerGrillClearcoatRoughness = action.payload
    },
    setSpeakerGrillReflectivity: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).speakerGrillReflectivity = action.payload
    },

    // ── Device materials — Charging connector tongue ────────────
    setChargingConnectorTongueColor: (state, action: PayloadAction<string>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorTongueColor = action.payload
    },
    setChargingConnectorTongueMetalness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorTongueMetalness = action.payload
    },
    setChargingConnectorTongueRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorTongueRoughness = action.payload
    },
    setChargingConnectorTongueClearcoat: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorTongueClearcoat = action.payload
    },
    setChargingConnectorTongueClearcoatRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorTongueClearcoatRoughness = action.payload
    },
    setChargingConnectorTongueReflectivity: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorTongueReflectivity = action.payload
    },

    // ── Device materials — Charging connector rim ───────────────
    setChargingConnectorRimColor: (state, action: PayloadAction<string>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorRimColor = action.payload
    },
    setChargingConnectorRimMetalness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorRimMetalness = action.payload
    },
    setChargingConnectorRimRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorRimRoughness = action.payload
    },
    setChargingConnectorRimClearcoat: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorRimClearcoat = action.payload
    },
    setChargingConnectorRimClearcoatRoughness: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorRimClearcoatRoughness = action.payload
    },
    setChargingConnectorRimReflectivity: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).chargingConnectorRimReflectivity = action.payload
    },

    // ── Device body surface textures ─────────────────────────────
    setBodyNoiseIntensity: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).bodyNoiseIntensity = action.payload
    },
    setBodyNormalStrength: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).bodyNormalStrength = action.payload
    },
    setBodyNormalPattern: (state, action: PayloadAction<BodyNormalPattern>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).bodyNormalPattern = action.payload
    },
    setButtonsNoiseIntensity: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).buttonsNoiseIntensity = action.payload
    },
    setButtonsNormalStrength: (state, action: PayloadAction<number>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).buttonsNormalStrength = action.payload
    },
    setButtonsNormalPattern: (state, action: PayloadAction<BodyNormalPattern>) => {
      active(state).activeDeviceName = CUSTOM_DEVICE_NAME
      active(state).buttonsNormalPattern = action.payload
    },

    // ── Device preset ─────────────────────────────────────────────
    applyDeviceConfig: (state, action: PayloadAction<DeviceConfig>) => {
      const d = action.payload
      const s = active(state)
      s.activeDeviceName = d.name
      // Dimensions
      s.deviceWidth = d.body.width
      s.deviceHeight = d.body.height
      s.deviceDepth = d.body.depth
      s.deviceCornerRadius = d.body.cornerRadius
      s.deviceEdgeBevel = d.body.edgeBevel
      s.frontFaceInset = d.frontFace.inset
      s.displayCornerRadius = d.display.cornerRadius
      s.displayInsetX = d.display.insetX
      s.displayInsetY = d.display.insetY
      // Features
      s.frontFaceFeatures = d.frontFaceFeatures
      s.sideEdgeFeatures = d.sideEdgeFeatures
      // Materials — Body
      s.bodyColor = d.materials.body.color
      s.bodyMetalness = d.materials.body.metalness
      s.bodyRoughness = d.materials.body.roughness
      s.bodyClearcoat = d.materials.body.clearcoat
      s.bodyClearcoatRoughness = d.materials.body.clearcoatRoughness
      s.bodyReflectivity = 0.5
      // Materials — Front face
      s.frontFaceColor = d.materials.frontFace.color
      s.frontFaceMetalness = d.materials.frontFace.metalness
      s.frontFaceRoughness = d.materials.frontFace.roughness
      s.frontFaceClearcoat = d.materials.frontFace.clearcoat
      s.frontFaceClearcoatRoughness = d.materials.frontFace.clearcoatRoughness
      s.frontFaceReflectivity = d.materials.frontFace.reflectivity
      // Materials — Buttons (default to body material)
      s.buttonsColor = d.materials.body.color
      s.buttonsMetalness = d.materials.body.metalness
      s.buttonsRoughness = d.materials.body.roughness
      s.buttonsClearcoat = d.materials.body.clearcoat
      s.buttonsClearcoatRoughness = d.materials.body.clearcoatRoughness
      s.buttonsReflectivity = 0.5
      // Materials — Antennas
      s.antennasColor = d.materials.antenna.color
      s.antennasMetalness = d.materials.antenna.metalness
      s.antennasRoughness = d.materials.antenna.roughness
      s.antennasClearcoat = d.materials.antenna.clearcoat
      s.antennasClearcoatRoughness = d.materials.antenna.clearcoatRoughness
      s.antennasReflectivity = 0.5
      // Materials — Screws
      s.screwsColor = d.materials.screw.color
      s.screwsMetalness = d.materials.screw.metalness
      s.screwsRoughness = d.materials.screw.roughness
      s.screwsClearcoat = d.materials.screw.clearcoat ?? 0
      s.screwsClearcoatRoughness = d.materials.screw.clearcoatRoughness ?? 0
      s.screwsReflectivity = d.materials.screw.reflectivity ?? 0.5
      // Materials — Front speaker
      s.frontSpeakerColor = d.materials.frontSpeaker.color
      s.frontSpeakerMetalness = d.materials.frontSpeaker.metalness
      s.frontSpeakerRoughness = d.materials.frontSpeaker.roughness
      s.frontSpeakerClearcoat = d.materials.frontSpeaker.clearcoat
      s.frontSpeakerClearcoatRoughness = d.materials.frontSpeaker.clearcoatRoughness
      s.frontSpeakerReflectivity = d.materials.frontSpeaker.reflectivity
      // Materials — Speaker grill
      s.speakerGrillColor = d.materials.speakerGrill.color
      s.speakerGrillMetalness = d.materials.speakerGrill.metalness
      s.speakerGrillRoughness = d.materials.speakerGrill.roughness
      s.speakerGrillClearcoat = d.materials.speakerGrill.clearcoat ?? 0
      s.speakerGrillClearcoatRoughness = d.materials.speakerGrill.clearcoatRoughness ?? 0
      s.speakerGrillReflectivity = d.materials.speakerGrill.reflectivity ?? 0.5
      // Materials — Charging connector tongue (defaults match prior hardcoded look)
      s.chargingConnectorTongueColor = d.materials.chargingConnectorTongue?.color ?? '#B5AFA6'
      s.chargingConnectorTongueMetalness = d.materials.chargingConnectorTongue?.metalness ?? 0.55
      s.chargingConnectorTongueRoughness = d.materials.chargingConnectorTongue?.roughness ?? 0.38
      s.chargingConnectorTongueClearcoat = d.materials.chargingConnectorTongue?.clearcoat ?? 0.3
      s.chargingConnectorTongueClearcoatRoughness = d.materials.chargingConnectorTongue?.clearcoatRoughness ?? 0.4
      s.chargingConnectorTongueReflectivity = d.materials.chargingConnectorTongue?.reflectivity ?? 0.5
      // Materials — Charging connector rim
      s.chargingConnectorRimColor = d.materials.chargingConnectorRim?.color ?? '#6e6b66'
      s.chargingConnectorRimMetalness = d.materials.chargingConnectorRim?.metalness ?? 0.45
      s.chargingConnectorRimRoughness = d.materials.chargingConnectorRim?.roughness ?? 0.55
      s.chargingConnectorRimClearcoat = d.materials.chargingConnectorRim?.clearcoat ?? 0.1
      s.chargingConnectorRimClearcoatRoughness = d.materials.chargingConnectorRim?.clearcoatRoughness ?? 0.9
      s.chargingConnectorRimReflectivity = d.materials.chargingConnectorRim?.reflectivity ?? 0.5
      // Surface textures
      s.bodyNoiseIntensity = d.surfaceTexture?.noiseIntensity ?? 0
      s.bodyNormalStrength = d.surfaceTexture?.normalStrength ?? 0
      s.bodyNormalPattern = (d.surfaceTexture?.normalPattern as BodyNormalPattern) ?? 'brushed'
      s.buttonsNoiseIntensity = d.surfaceTexture?.noiseIntensity ?? 0
      s.buttonsNormalStrength = d.surfaceTexture?.normalStrength ?? 0
      s.buttonsNormalPattern = (d.surfaceTexture?.normalPattern as BodyNormalPattern) ?? 'brushed'
      // Built-in decals shipped with the device — replace the scene's decals (consistent with
      // how features above are replaced). Deep-cloned so scenes don't share refs.
      s.customDecals = d.customDecals ? (JSON.parse(JSON.stringify(d.customDecals)) as CustomDecal[]) : []
      clampCustomDecals(s)
      // Bump geometry version to trigger worker recomputation
      s.geometryVersion += 1
    },

    // ── Scene preset ──────────────────────────────────────────────
    applyScenePreset: (state, action: PayloadAction<SceneFullConfig>) => {
      const p = action.payload
      const s = active(state)
      // Background (supports both new {mode, gradientStops} and legacy {color1, color2} formats)
      const bg = p.background as unknown as Record<string, unknown>
      s.bgMode = (bg.mode as BgMode) ?? 'gradient'
      s.bgGradientStops = (bg.gradientStops as typeof s.bgGradientStops) ?? [
        {id: 'stop-0', color: (bg.color1 as string) ?? '#ffffff', position: 0},
        {id: 'stop-1', color: (bg.color2 as string) ?? '#e0e7ff', position: 1},
      ]
      s.bgAngle = p.background.angle
      s.bgImageKey = p.background.imageKey ?? ''
      if (p.background.imageUrl) s.bgImageUrl = p.background.imageUrl
      if (p.background.imageFit) s.bgImageFit = p.background.imageFit
      if (p.background.imageOffsetX !== undefined) s.bgImageOffsetX = p.background.imageOffsetX
      if (p.background.imageOffsetY !== undefined) s.bgImageOffsetY = p.background.imageOffsetY
      if (p.background.imageZoom !== undefined) s.bgImageZoom = p.background.imageZoom
      // Lighting
      s.lights = p.lighting.lights as SceneLightConfig[]
      s.ambientIntensity = p.lighting.ambient.intensity
      s.ambientColor = p.lighting.ambient.color
      // Environment
      s.envPreset = p.environment.preset ?? 'city'
      s.envMapIntensity = p.environment.envMapIntensity
      // Ground
      s.groundDistance = p.ground.offset
      // Camera
      s.cameraPosition = p.camera.position
      s.cameraRotation = p.camera.rotation ?? [0, 0, 0]
      // Device Z-rotation + position — mirrors loadProject so puppeteer thumbnails
      // and render-history items don't drop them. Any new scene field used by the
      // render-app must live in BOTH reducers.
      if (typeof p.deviceRotationZ === 'number') {
        s.deviceRotationZ = ((p.deviceRotationZ % 360) + 360) % 360
      }
      if (typeof p.devicePositionZ === 'number') {
        s.devicePositionZ = clampDevicePositionZ(p.devicePositionZ)
      }
      // 2D Text — replace with preset texts (or clear)
      s.texts = p.text2d ? (p.text2d as ScreenText[]) : []
      // 3D Text — replace with preset texts (or clear)
      s.texts3d = p.text3d ? (JSON.parse(JSON.stringify(p.text3d)) as SceneText3D[]) : []
      // 2D Sprites — replace with preset sprites (or clear)
      s.sprites2d = p.sprites2d ? (p.sprites2d as ScreenSprite[]) : []
      // 3D Sprites — replace with preset sprites (or clear)
      s.sprites3d = p.sprites3d ? (JSON.parse(JSON.stringify(p.sprites3d)) as SceneSprite3D[]) : []
      // Custom decals — replace with preset decals (or clear). Mirrors loadProject so
      // puppeteer thumbnails / render-history items keep the decals.
      s.customDecals = p.customDecals ? (JSON.parse(JSON.stringify(p.customDecals)) as CustomDecal[]) : []
      // Screen image
      if (p.textureUrl) {
        s.screenImageUrl = p.textureUrl
      }
      // Export size
      if (p.export) {
        s.exportWidth = clampExportDim(p.export.width)
        s.exportHeight = clampExportDim(p.export.height)
        s.exportCarouselEnabled = p.export.carousel ?? false
        s.exportCarouselCount = clampCarouselCount(p.export.carouselCount ?? DEFAULT_CAROUSEL_COUNT)
        s.exportCarouselGap = clampCarouselGap(p.export.carouselGap ?? DEFAULT_CAROUSEL_GAP_PCT)
      }
      // Screen-glass reflection blend
      if (typeof p.displayReflection === 'number') {
        s.displayReflection = Math.max(0, Math.min(1, p.displayReflection))
      }
      // Device overrides (dimensions, materials, features)
      if (p.device) {
        const d = p.device
        s.activeDeviceName = d.name
        s.deviceWidth = d.body.width
        s.deviceHeight = d.body.height
        s.deviceDepth = d.body.depth
        s.deviceCornerRadius = d.body.cornerRadius
        s.deviceEdgeBevel = d.body.edgeBevel
        s.frontFaceInset = d.frontFace.inset
        s.displayCornerRadius = d.display.cornerRadius
        s.displayInsetX = d.display.insetX
        s.displayInsetY = d.display.insetY
        s.frontFaceFeatures = d.frontFaceFeatures
        s.sideEdgeFeatures = d.sideEdgeFeatures
        if (d.materials?.body) {
          s.bodyColor = d.materials.body.color
          s.bodyMetalness = d.materials.body.metalness
          s.bodyRoughness = d.materials.body.roughness
          s.bodyClearcoat = d.materials.body.clearcoat
          s.bodyClearcoatRoughness = d.materials.body.clearcoatRoughness
        }
        if (d.materials?.frontFace) {
          s.frontFaceColor = d.materials.frontFace.color
          s.frontFaceMetalness = d.materials.frontFace.metalness
          s.frontFaceRoughness = d.materials.frontFace.roughness
          s.frontFaceClearcoat = d.materials.frontFace.clearcoat
          s.frontFaceClearcoatRoughness = d.materials.frontFace.clearcoatRoughness
          s.frontFaceReflectivity = d.materials.frontFace.reflectivity
        }
        if (d.materials?.buttons) {
          s.buttonsColor = d.materials.buttons.color
          s.buttonsMetalness = d.materials.buttons.metalness
          s.buttonsRoughness = d.materials.buttons.roughness
          s.buttonsClearcoat = d.materials.buttons.clearcoat
          s.buttonsClearcoatRoughness = d.materials.buttons.clearcoatRoughness
          s.buttonsReflectivity = d.materials.buttons.reflectivity
        }
        if (d.materials?.antenna) {
          s.antennasColor = d.materials.antenna.color
          s.antennasMetalness = d.materials.antenna.metalness
          s.antennasRoughness = d.materials.antenna.roughness
          s.antennasClearcoat = d.materials.antenna.clearcoat
          s.antennasClearcoatRoughness = d.materials.antenna.clearcoatRoughness
        }
        if (d.surfaceTexture) {
          s.bodyNoiseIntensity = d.surfaceTexture.noiseIntensity
          s.bodyNormalStrength = d.surfaceTexture.normalStrength
          s.bodyNormalPattern = d.surfaceTexture.normalPattern as BodyNormalPattern
        }
        if (d.buttonsSurfaceTexture) {
          s.buttonsNoiseIntensity = d.buttonsSurfaceTexture.noiseIntensity
          s.buttonsNormalStrength = d.buttonsSurfaceTexture.normalStrength
          s.buttonsNormalPattern = d.buttonsSurfaceTexture.normalPattern as BodyNormalPattern
        }
        // Bump geometry version to trigger worker recomputation
        s.geometryVersion += 1
      }
    },
  },
})

// ── Selectors ─────────────────────────────────────────────────────

/** Root state shape used by selectors (avoids circular import with store.ts) */
interface RootStateShape {
  present: {scenes: ScenesState}
}

/** Select the active scene's state */
export const selectActiveScene = (state: RootStateShape): SingleSceneState =>
  state.present.scenes.scenes[state.present.scenes.activeSceneIndex]

/** Select a scene by index */
export const selectSceneByIndex =
  (index: number) =>
  (state: RootStateShape): SingleSceneState =>
    state.present.scenes.scenes[index]

/** Select all scenes */
export const selectAllScenes = (state: RootStateShape): SingleSceneState[] => state.present.scenes.scenes

/** Select the active scene index */
export const selectActiveSceneIndex = (state: RootStateShape): number => state.present.scenes.activeSceneIndex

/** Select scene count */
export const selectSceneCount = (state: RootStateShape): number => state.present.scenes.scenes.length

/** Select all scene names */
export const selectSceneNames = (state: RootStateShape): string[] => state.present.scenes.sceneNames

/** Helper: get the size a feature occupies along the edge */
const getFeatureEdgeSize = (f: SideEdgeFeatureConfig): number => {
  switch (f.type) {
    case 'sideButton':
    case 'muteSwitch':
      return f.height
    case 'antenna':
      return f.width
    case 'screw':
      return f.diameter
    case 'sideSpeakerGrill':
      return f.height
    case 'chargingConnector':
      return f.width
    case 'simCardSlot':
      return f.height
  }
}

/** Clamp a side-edge feature's position to the valid range for its edge, using its
 *  along-edge half extent — so growing the feature pushes its center inward. Shared
 *  by the `updateSideEdgeFeature` reducer and the live drag preview (ghost). */
export const clampSideFeaturePosition = (
  f: SideEdgeFeatureConfig,
  edge: SideEdge,
  deviceWidth: number,
  deviceHeight: number,
  cornerRadius: number,
): number => {
  const edgeLen = edge === 'left' || edge === 'right' ? deviceHeight : deviceWidth
  const maxPos = Math.max(0, edgeLen / 2 - cornerRadius - getFeatureEdgeSize(f) / 2)
  return Math.max(-maxPos, Math.min(maxPos, f.position))
}

/** Get the minimum front face width/height a single front feature requires */
const getFrontFeatureMinSize = (f: FrontFaceFeatureConfig): {minW: number; minH: number} => {
  switch (f.type) {
    case 'homeButton':
      return {minW: f.radius * 2, minH: f.radius * 2}
    case 'cameraLens':
      return {minW: f.radius * 2, minH: f.radius * 2}
    case 'displayCutout':
      return {minW: f.width, minH: f.height}
    case 'homeIndicator':
      return {minW: f.width, minH: f.height}
    case 'frontSpeaker':
      return {minW: f.width, minH: f.height}
  }
}

/** Select minimum device dimensions based on side edge and front face feature sizes.
 *  For width: max total feature size across top/bottom edges + 2 × cornerRadius,
 *  plus the minimum needed to contain the largest front face feature.
 *  For height: same logic for left/right edges and front face features. */
let _lastMinDims: {minWidth: number; minHeight: number} | null = null

export const selectMinDeviceDimensions = (state: RootStateShape): {minWidth: number; minHeight: number} => {
  const s = selectActiveScene(state)
  const cr = s.deviceCornerRadius
  const gap = FRONT_FACE_GAP
  const clampedBevel = Math.min(s.deviceEdgeBevel, s.deviceWidth / 2, s.deviceHeight / 2)
  const BASE_MIN_W = 30
  const BASE_MIN_H = 60

  // Side edge feature minimums
  const topTotal = s.sideEdgeFeatures.top.reduce((sum, f) => sum + getFeatureEdgeSize(f), 0)
  const bottomTotal = s.sideEdgeFeatures.bottom.reduce((sum, f) => sum + getFeatureEdgeSize(f), 0)
  const edgeMinW = Math.max(topTotal, bottomTotal) + 2 * cr

  const leftTotal = s.sideEdgeFeatures.left.reduce((sum, f) => sum + getFeatureEdgeSize(f), 0)
  const rightTotal = s.sideEdgeFeatures.right.reduce((sum, f) => sum + getFeatureEdgeSize(f), 0)
  const edgeMinH = Math.max(leftTotal, rightTotal) + 2 * cr

  // Front face feature minimums — convert from front face size to device dimension
  const ffPadding = 2 * (clampedBevel + s.frontFaceInset + gap)
  let frontMinW = 0
  let frontMinH = 0
  for (const f of s.frontFaceFeatures) {
    const {minW, minH} = getFrontFeatureMinSize(f)
    frontMinW = Math.max(frontMinW, minW + ffPadding)
    frontMinH = Math.max(frontMinH, minH + ffPadding)
  }

  const minWidth = Math.max(BASE_MIN_W, Math.ceil(Math.max(edgeMinW, frontMinW) * 10) / 10)
  const minHeight = Math.max(BASE_MIN_H, Math.ceil(Math.max(edgeMinH, frontMinH) * 10) / 10)

  // Return cached reference if values haven't changed — avoids React-Redux rerender warnings
  if (_lastMinDims && _lastMinDims.minWidth === minWidth && _lastMinDims.minHeight === minHeight) {
    return _lastMinDims
  }
  _lastMinDims = {minWidth, minHeight}
  return _lastMinDims
}

// ── Exports ───────────────────────────────────────────────────────

export const {
  // Initialization
  initializeWithDevice,
  loadProject,
  // Scene management
  setActiveScene,
  addScene,
  addNewScene,
  removeScene,
  renameScene,
  // Background
  setBgMode,
  addGradientStop,
  removeGradientStop,
  updateGradientStop,
  setBgAngle,
  setBgImageKey,
  setBgImageUrl,
  setBgImageUploaded,
  setBgImageFit,
  setBgImageOffset,
  setBgImageZoom,
  // Lighting
  updateLight,
  setAmbientIntensity,
  setAmbientColor,
  // Environment
  setEnvPreset,
  setEnvMapIntensity,
  // Ground
  setGroundDistance,
  // Screen image
  setScreenImageUrl,
  setScreenImageUploaded,
  // Export
  setExportWidth,
  setExportHeight,
  setExportSize,
  setExportFrameEnabled,
  setExportCarouselEnabled,
  setExportCarouselCount,
  setExportCarouselGap,
  // Device transform
  setDeviceRotationZ,
  setDevicePositionZ,
  // 2D Texts
  addText,
  removeText,
  updateText,
  // 3D Texts
  addText3D,
  removeText3D,
  updateText3D,
  // 2D Sprites
  addSprite2D,
  removeSprite2D,
  updateSprite2D,
  setSprite2DUploaded,
  // 3D Sprites
  addSprite3D,
  removeSprite3D,
  updateSprite3D,
  updateSprite3DMaterial,
  setSprite3DUploaded,
  // Custom decals
  addCustomDecal,
  removeCustomDecal,
  updateCustomDecal,
  updateCustomDecalMaterial,
  setCustomDecalUploaded,
  // Camera
  setCameraPosition,
  setCameraPositionForScene,
  // Device dimensions
  setDeviceWidth,
  setDeviceHeight,
  setDeviceDepth,
  setDeviceCornerRadius,
  setDeviceEdgeBevel,
  setFrontFaceInset,
  setDisplayCornerRadius,
  setDisplayInsetX,
  setDisplayInsetY,
  setDisplayReflection,
  // Front face features
  addFrontFaceFeature,
  removeFrontFaceFeature,
  updateFrontFaceFeature,
  // Side edge features
  addSideEdgeFeature,
  removeSideEdgeFeature,
  updateSideEdgeFeature,
  // Body materials
  setBodyColor,
  setBodyMetalness,
  setBodyRoughness,
  setBodyClearcoat,
  setBodyClearcoatRoughness,
  setBodyReflectivity,
  // Front face materials
  setFrontFaceColor,
  setFrontFaceMetalness,
  setFrontFaceRoughness,
  setFrontFaceClearcoat,
  setFrontFaceClearcoatRoughness,
  setFrontFaceReflectivity,
  // Buttons materials
  setButtonsColor,
  setButtonsMetalness,
  setButtonsRoughness,
  setButtonsClearcoat,
  setButtonsClearcoatRoughness,
  setButtonsReflectivity,
  // Antennas materials
  setAntennasColor,
  setAntennasMetalness,
  setAntennasRoughness,
  setAntennasClearcoat,
  setAntennasClearcoatRoughness,
  setAntennasReflectivity,
  // Screws materials
  setScrewsColor,
  setScrewsMetalness,
  setScrewsRoughness,
  setScrewsClearcoat,
  setScrewsClearcoatRoughness,
  setScrewsReflectivity,
  // Front speaker materials
  setFrontSpeakerColor,
  setFrontSpeakerMetalness,
  setFrontSpeakerRoughness,
  setFrontSpeakerClearcoat,
  setFrontSpeakerClearcoatRoughness,
  setFrontSpeakerReflectivity,
  // Speaker grill materials
  setSpeakerGrillColor,
  setSpeakerGrillMetalness,
  setSpeakerGrillRoughness,
  setSpeakerGrillClearcoat,
  setSpeakerGrillClearcoatRoughness,
  setSpeakerGrillReflectivity,
  // Charging connector tongue materials
  setChargingConnectorTongueColor,
  setChargingConnectorTongueMetalness,
  setChargingConnectorTongueRoughness,
  setChargingConnectorTongueClearcoat,
  setChargingConnectorTongueClearcoatRoughness,
  setChargingConnectorTongueReflectivity,
  // Charging connector rim materials
  setChargingConnectorRimColor,
  setChargingConnectorRimMetalness,
  setChargingConnectorRimRoughness,
  setChargingConnectorRimClearcoat,
  setChargingConnectorRimClearcoatRoughness,
  setChargingConnectorRimReflectivity,
  // Device body surface textures
  setBodyNoiseIntensity,
  setBodyNormalStrength,
  setBodyNormalPattern,
  setButtonsNoiseIntensity,
  setButtonsNormalStrength,
  setButtonsNormalPattern,
  // Device preset
  applyDeviceConfig,
  // Scene preset
  applyScenePreset,
} = scenesSlice.actions

export default scenesSlice.reducer
